import { type ReactNode, Suspense } from "react"

import { NeuralNetworkLoader } from "@/shared/ui/loading"
import { TabModal, type TabModalProps } from "@/shared/ui/tab-modal"

type TabModalVisualProps = Pick<
  TabModalProps,
  | "headerActions"
  | "titleAdornment"
  | "titleClassName"
  | "subtitle"
  | "bodyClassName"
  | "tabNavigation"
  | "height"
  | "disableDrag"
  | "isBlocking"
  | "showCloseButton"
  | "preserveScrollPosition"
  | "isLoading"
  | "onBeforeClose"
  | "maxWidth"
>

export type ModalHeight = TabModalProps["height"]

export interface ModalShellProps extends TabModalVisualProps {
  isOpen: boolean
  onClose: () => void
  title: string
  caption?: string
  icon?: ReactNode
  children: ReactNode
  loaderFallback?: ReactNode
  isLoading?: boolean
}

const DefaultLoader = () => (
  <div className="flex w-full justify-center py-16">
    <NeuralNetworkLoader className="max-w-sm" />
  </div>
)

/**
 * ModalShell renders the TabModal chrome immediately and defers body rendering
 * via Suspense so feature logic can stay outside the shared UI layer.
 */
export const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  onClose,
  title,
  caption,
  icon,
  titleAdornment,
  titleClassName,
  subtitle,
  headerActions,
  children,
  bodyClassName,
  tabNavigation,
  height,
  disableDrag,
  isBlocking,
  showCloseButton,
  preserveScrollPosition,
  onBeforeClose,
  loaderFallback,
  isLoading,
  maxWidth
}) => {
  const fallback = loaderFallback ?? <DefaultLoader />

  return (
    <TabModal
      isOpen={isOpen}
      onClose={onClose}
      onBeforeClose={onBeforeClose}
      title={title}
      caption={caption}
      icon={icon}
      titleAdornment={titleAdornment}
      titleClassName={titleClassName}
      subtitle={subtitle}
      headerActions={headerActions}
      bodyClassName={bodyClassName}
      tabNavigation={tabNavigation}
      height={height}
      disableDrag={disableDrag}
      isBlocking={isBlocking}
      showCloseButton={showCloseButton}
      preserveScrollPosition={preserveScrollPosition}
      maxWidth={maxWidth}
      isLoading={isLoading}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </TabModal>
  )
}
